import React, { useState, useEffect, useCallback } from 'react'
import {
  Languages,
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  Filter,
  Upload,
  ChevronDown,
  ChevronRight,
  Globe
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import Toast, { ToastContainer } from '../../components/admin/Toast'

// Provider configuration
const providerConfig = {
  openai: { label: 'OpenAI', color: 'bg-green-500' },
  anthropic: { label: 'Anthropic', color: 'bg-orange-500' },
  gemini: { label: 'Gemini', color: 'bg-blue-500' },
  grok: { label: 'Grok', color: 'bg-purple-500' },
  deepseek: { label: 'DeepSeek', color: 'bg-cyan-500' },
}

// Common languages for bulk add
const commonLanguages = [
  { locale: 'en-US', name: 'English (US)' },
  { locale: 'en-GB', name: 'English (UK)' },
  { locale: 'de-DE', name: 'German' },
  { locale: 'fr-FR', name: 'French' },
  { locale: 'es-ES', name: 'Spanish' },
  { locale: 'it-IT', name: 'Italian' },
  { locale: 'pt-PT', name: 'Portuguese' },
  { locale: 'pt-BR', name: 'Portuguese (Brazil)' },
  { locale: 'nl-NL', name: 'Dutch' },
  { locale: 'pl-PL', name: 'Polish' },
  { locale: 'cs-CZ', name: 'Czech' },
  { locale: 'sk-SK', name: 'Slovak' },
  { locale: 'hu-HU', name: 'Hungarian' },
  { locale: 'ro-RO', name: 'Romanian' },
  { locale: 'bg-BG', name: 'Bulgarian' },
  { locale: 'hr-HR', name: 'Croatian' },
  { locale: 'sl-SI', name: 'Slovenian' },
  { locale: 'et-EE', name: 'Estonian' },
  { locale: 'lv-LV', name: 'Latvian' },
  { locale: 'lt-LT', name: 'Lithuanian' },
  { locale: 'fi-FI', name: 'Finnish' },
  { locale: 'sv-SE', name: 'Swedish' },
  { locale: 'da-DK', name: 'Danish' },
  { locale: 'no-NO', name: 'Norwegian' },
  { locale: 'ru-RU', name: 'Russian' },
  { locale: 'uk-UA', name: 'Ukrainian' },
  { locale: 'el-GR', name: 'Greek' },
  { locale: 'tr-TR', name: 'Turkish' },
  { locale: 'ar-SA', name: 'Arabic' },
  { locale: 'he-IL', name: 'Hebrew' },
  { locale: 'zh-CN', name: 'Chinese (Simplified)' },
  { locale: 'zh-TW', name: 'Chinese (Traditional)' },
  { locale: 'ja-JP', name: 'Japanese' },
  { locale: 'ko-KR', name: 'Korean' },
  { locale: 'th-TH', name: 'Thai' },
  { locale: 'vi-VN', name: 'Vietnamese' },
  { locale: 'id-ID', name: 'Indonesian' },
  { locale: 'ms-MY', name: 'Malay' },
  { locale: 'hi-IN', name: 'Hindi' },
]

const LanguagesPage = () => {
  // State
  const [languages, setLanguages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filterProvider, setFilterProvider] = useState('all')

  // Expanded providers
  const [expandedProviders, setExpandedProviders] = useState({})

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showBulkAddModal, setShowBulkAddModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, language: null })
  const [isDeleting, setIsDeleting] = useState(false)
  const [isAdding, setIsAdding] = useState(false)

  // Add form state
  const [newLanguage, setNewLanguage] = useState({ provider: '', locale: '', name: '' })
  const [bulkAddProvider, setBulkAddProvider] = useState('')
  const [selectedBulkLanguages, setSelectedBulkLanguages] = useState([])

  // Toast state
  const [toasts, setToasts] = useState([])

  // Add toast helper
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Fetch languages
  const fetchLanguages = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminApi.getLanguages()
      setLanguages(data)
    } catch (err) {
      console.error('Failed to fetch languages:', err)
      setError(err.response?.data?.detail || 'Failed to load languages')
      addToast('Failed to load languages', 'error')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    fetchLanguages()
  }, [fetchLanguages])

  // Group languages by provider
  const groupedLanguages = languages.reduce((acc, lang) => {
    const provider = lang.provider || 'global'
    if (!acc[provider]) {
      acc[provider] = []
    }
    acc[provider].push(lang)
    return acc
  }, {})

  // Filter by provider
  const filteredProviders = filterProvider === 'all'
    ? Object.keys(groupedLanguages)
    : [filterProvider].filter(p => groupedLanguages[p])

  // Toggle provider expansion
  const toggleProvider = (provider) => {
    setExpandedProviders(prev => ({
      ...prev,
      [provider]: !prev[provider]
    }))
  }

  // Handle add single language
  const handleAddLanguage = async () => {
    if (!newLanguage.provider || !newLanguage.locale || !newLanguage.name) {
      addToast('Please fill in all fields', 'error')
      return
    }

    try {
      setIsAdding(true)
      await adminApi.bulkCreateLanguages(newLanguage.provider, [
        { locale: newLanguage.locale, name: newLanguage.name }
      ])
      addToast('Language added successfully', 'success')
      setShowAddModal(false)
      setNewLanguage({ provider: '', locale: '', name: '' })
      fetchLanguages()
    } catch (err) {
      console.error('Failed to add language:', err)
      addToast(err.response?.data?.detail || 'Failed to add language', 'error')
    } finally {
      setIsAdding(false)
    }
  }

  // Handle bulk add languages
  const handleBulkAdd = async () => {
    if (!bulkAddProvider || selectedBulkLanguages.length === 0) {
      addToast('Please select a provider and at least one language', 'error')
      return
    }

    try {
      setIsAdding(true)
      const languagesToAdd = selectedBulkLanguages.map(locale => {
        const lang = commonLanguages.find(l => l.locale === locale)
        return { locale, name: lang?.name || locale }
      })
      await adminApi.bulkCreateLanguages(bulkAddProvider, languagesToAdd)
      addToast(`Added ${languagesToAdd.length} languages successfully`, 'success')
      setShowBulkAddModal(false)
      setBulkAddProvider('')
      setSelectedBulkLanguages([])
      fetchLanguages()
    } catch (err) {
      console.error('Failed to bulk add languages:', err)
      addToast(err.response?.data?.detail || 'Failed to add languages', 'error')
    } finally {
      setIsAdding(false)
    }
  }

  // Handle delete language
  const handleDelete = async () => {
    const language = deleteConfirm.language
    if (!language) return

    try {
      setIsDeleting(true)
      await adminApi.deleteLanguage(language.id)
      addToast('Language deleted successfully', 'success')
      setDeleteConfirm({ open: false, language: null })
      fetchLanguages()
    } catch (err) {
      console.error('Failed to delete language:', err)
      addToast(err.response?.data?.detail || 'Failed to delete language', 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  // Render loading state
  if (loading && languages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading languages...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="languages-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Languages className="w-6 h-6" />
            Supported Languages
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage supported languages for each LLM provider
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchLanguages()}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={() => setShowBulkAddModal(true)}
            data-testid="bulk-add-languages-button"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Add
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="add-language-button"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Language
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center text-red-800">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={filterProvider}
            onChange={(e) => setFilterProvider(e.target.value)}
            data-testid="provider-filter"
            className="rounded-md border-gray-300 shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Providers</option>
            {Object.entries(providerConfig).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>
        <span className="text-sm text-gray-500" data-testid="languages-count">
          {languages.length} languages total
        </span>
      </div>

      {/* Provider Groups */}
      <div className="space-y-4">
        {filteredProviders.map(provider => {
          const providerLangs = groupedLanguages[provider] || []
          const isExpanded = expandedProviders[provider] !== false // Default expanded
          const config = providerConfig[provider] || { label: provider, color: 'bg-gray-500' }

          return (
            <div key={provider} className="bg-white rounded-lg shadow border" data-testid={`provider-group-${provider}`}>
              {/* Provider Header */}
              <button
                onClick={() => toggleProvider(provider)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <div className={clsx('w-3 h-3 rounded-full', config.color)} />
                  <span className="font-medium text-gray-900">{config.label}</span>
                  <span className="text-sm text-gray-500">({providerLangs.length} languages)</span>
                </div>
                <Globe className="w-5 h-5 text-gray-400" />
              </button>

              {/* Languages List */}
              {isExpanded && (
                <div className="border-t px-4 py-3">
                  {providerLangs.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No languages configured</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {providerLangs.map(lang => (
                        <div
                          key={lang.id}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-md group hover:bg-gray-100"
                        >
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-gray-900 truncate">{lang.name}</div>
                            <div className="text-xs text-gray-500">{lang.locale}</div>
                          </div>
                          <button
                            onClick={() => setDeleteConfirm({ open: true, language: lang })}
                            className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Add Single Language Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Add Language</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <select
                  value={newLanguage.provider}
                  onChange={(e) => setNewLanguage(prev => ({ ...prev, provider: e.target.value }))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Provider</option>
                  {Object.entries(providerConfig).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Locale Code</label>
                <input
                  type="text"
                  value={newLanguage.locale}
                  onChange={(e) => setNewLanguage(prev => ({ ...prev, locale: e.target.value }))}
                  placeholder="e.g., en-US, de-DE"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language Name</label>
                <input
                  type="text"
                  value={newLanguage.name}
                  onChange={(e) => setNewLanguage(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., English (US)"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLanguage}
                disabled={isAdding}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
              >
                {isAdding && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Add Language
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Bulk Add Languages</h3>
              <p className="text-sm text-gray-500 mt-1">Select a provider and languages to add</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <select
                  value={bulkAddProvider}
                  onChange={(e) => setBulkAddProvider(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Provider</option>
                  {Object.entries(providerConfig).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Languages</label>
                  <button
                    onClick={() => {
                      if (selectedBulkLanguages.length === commonLanguages.length) {
                        setSelectedBulkLanguages([])
                      } else {
                        setSelectedBulkLanguages(commonLanguages.map(l => l.locale))
                      }
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    {selectedBulkLanguages.length === commonLanguages.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto border rounded-md p-2">
                  {commonLanguages.map(lang => (
                    <label key={lang.locale} className="flex items-center p-1 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedBulkLanguages.includes(lang.locale)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBulkLanguages(prev => [...prev, lang.locale])
                          } else {
                            setSelectedBulkLanguages(prev => prev.filter(l => l !== lang.locale))
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{lang.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedBulkLanguages.length} selected
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkAddModal(false)
                  setBulkAddProvider('')
                  setSelectedBulkLanguages([])
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAdd}
                disabled={isAdding || !bulkAddProvider || selectedBulkLanguages.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
              >
                {isAdding && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Add {selectedBulkLanguages.length} Languages
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        title="Delete Language"
        message={`Are you sure you want to delete "${deleteConfirm.language?.name}" (${deleteConfirm.language?.locale}) from ${deleteConfirm.language?.provider || 'global'}?`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ open: false, language: null })}
      />

      {/* Toast notifications */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  )
}

export default LanguagesPage
